import React, { ReactNode } from "react";
import { Helmet } from "react-helmet";
import {useIntl, IntlProvider, FormattedMessage} from "react-intl";
import LocalizeContext from "./localize-context";
import { LocaleManager, ResolvedLocaleData } from "../../util/locale";
import Notification from "../components/notification";
import UnrecoverableError from "../components/unrecoverable-error";
import ProgressIndicator from "../components/progress-indicator/progress-indicator-container";

export interface LocalizeContainerProps {

  defaultNotAvailable: string | undefined;

  defaultMessagesReady: boolean;

  notAvailable: string | undefined;

  messages: { [key: string]: any } | undefined | null;
  changeLanguage: (l: string) => void;

  notAvailableDismissed: boolean;
  setNotAvailableDismissed: (b: boolean) => void;
  restoreStoredNotification: boolean;
  dismissRestoreStoredNotification: () => void;

  resolvedLocaleData: ResolvedLocaleData|undefined;
}

export interface LocalizeProps extends LocalizeContainerProps {
  children: ReactNode;

}
export function asIntlLocale(s: string) {
  const parts = s.toLowerCase().split(/-|_/);
  if (parts.length > 1) {
    parts[1] = parts[1].toUpperCase();
    return parts.join("-");
  } else {
    return parts[0];
  }
}
export function asDukeLocale(s: string) {
  const parts = s.toLowerCase().split(/-|_/);
  if (parts.length > 1) {
    return parts.join("-");
  } else {
    return parts[0];
  }
}

/**
 * Internal component to allow use of intl.formatMessage via useIntl
 * @constructor
 */
const WindowMeta = () => {
  const intl = useIntl();
  return (
      <Helmet>
        <title>{intl.formatMessage({
          defaultMessage: "Oh no! Language error",
          description: "Window meta title for unrecoverable localisation error screen",
        })}</title>
        <meta name="description" content={intl.formatMessage({
        defaultMessage: "Default language copy could not be located. Please contact administrator.",
        description: "Window meta description for unrecoverable localisation error screen"
      })} />
      </Helmet>);
}

export default function Localize(props: LocalizeProps) {
  const {
    defaultNotAvailable,
    defaultMessagesReady,
    notAvailable,
    messages,
    changeLanguage,
    notAvailableDismissed,
    setNotAvailableDismissed,
    restoreStoredNotification,
    dismissRestoreStoredNotification,
    resolvedLocaleData,
  } = props;
  const defaultLocale = resolvedLocaleData?.defaultLocale as string;
  const storedLocale = resolvedLocaleData?.stored;
  const language = resolvedLocaleData?.locale?.value as string;
  const defaultLocaleLabel = LocaleManager.resolveLocaleLabel(
    resolvedLocaleData?.defaultLocale as string
  );

  const languageNames = new Intl.DisplayNames(
    [!notAvailable ? asIntlLocale(language as string) : defaultLocale],
    { type: "language" }
  );
  return (
    <LocalizeContext.Provider
      value={{
        ...resolvedLocaleData,
        changeLanguage: changeLanguage,
      }}
    >
    <ProgressIndicator show={!defaultMessagesReady} backdrop={true} variant={"lg"} />
      <IntlProvider
        defaultLocale={asIntlLocale(defaultLocale)}
        locale={asIntlLocale(language as string)}
        messages={!!messages ? messages : undefined}
      >
        <Helmet htmlAttributes={{ lang: asIntlLocale(language as string) }} />
        {defaultMessagesReady && !!messages && props.children}
        {notAvailable && (
          <LocaleNotAvailableNotification
            show={notAvailableDismissed}
            onClose={() => {
              setNotAvailableDismissed(true);
            }}
            locale={notAvailable}
            def={defaultLocaleLabel}
            defCode={defaultLocale}
          />
        )}
        {!!defaultNotAvailable && (
            <UnrecoverableError
              id={"noDefaultLocale"}
              type={"danger"}
              category={"app"}
              show={true}
              asChild={true}
              data-test-no-default-locale
            >
              <WindowMeta />
              <h1 className={"alert-title"}>
                <FormattedMessage
                    defaultMessage="Oh no!"
                    description={"language error screen title"}
                />
              </h1>
              <FormattedMessage
                  defaultMessage="Default language copy could not be located. Please contact administrator."
                  description={
                    "language error screen message. The administrator here is not the same as the 'admin' used elsewhere, different translation is preferred. This refers to a person responsible for deploying and managing this application."
                  }
              />
            </UnrecoverableError>
        )}
        {restoreStoredNotification && (
            <Notification
                id="restoreStoredLocaleNotification"
                type={"info"}
                category={"app"}
                show={restoreStoredNotification}
                onClose={dismissRestoreStoredNotification}
                data-test-restore-stored-locale
            >
              <h4 className={"alert-title"}>
                <FormattedMessage
                  defaultMessage="Current and stored language settings do not match."
                  description="Notification title to show when the page is rendered in a language that is different than the one user has selected before."
                />
              </h4>
              <p>
                <FormattedMessage
                  defaultMessage="The page is in {currentLanguage}, but you've previously used {rememberedLanguage}. Please verify your language choice"
                  description="Notification copy to show when the page is rendered in a language that is different than the one user has selected before. 'currentLanguage' = name of current language, 'rememberedLanguage' = name of previously used language"
                  values={{
                    currentLanguage: languageNames.of(language),
                    rememberedLanguage: languageNames.of(storedLocale as string),
                  }}
                />
              </p>
              <button
                data-test-restore-stored-locale-button
                className={"btn btn-theme-to-primary mt-2 float-right ml-2 mt-2"}
                onClick={() => changeLanguage(storedLocale as string)}
              >
                {LocaleManager.resolveLocaleLabel(storedLocale as string, null) ||
                  languageNames.of(storedLocale as string)}
              </button>
              <button
                data-test-dismiss-restore-stored
                className={"btn btn-theme-to-primary mt-2 float-right ml-2 mt-2"}
                onClick={() => changeLanguage(language as string)}
              >
                {LocaleManager.resolveLocaleLabel(language)}
              </button>
            </Notification>
        )}
      </IntlProvider>
    </LocalizeContext.Provider>
  );
}

/**
 * declaring this internally used snippet as "component" to allow use of intl within.
 */
function LocaleNotAvailableNotification(props: {
  show: boolean;
  onClose: () => void;
  locale: string;
  def: string;
  defCode: string;
}) {
  const intl = useIntl();
  const languageNames = new Intl.DisplayNames([props.defCode], {
    type: "language",
  });

  return (
    <Notification
        id="localeNotAvailablePrompt"
        type={"danger"}
        category={"app"}
        show={!props.show}
        onClose={props.onClose}
        dismissible={true}
    >
      <h4 className={"alert-title"}>
        {intl.formatMessage(
          {
            defaultMessage: "The requested language {locale} is not available",
            description:
              "Notification heading for missing language. 'locale' = the requested locale code",
          },
          {
            locale: <strong>{languageNames.of(props.locale)}</strong>,
          }
        )}
      </h4>
      <p>
        {intl.formatMessage(
          {
            defaultMessage:
              "Rendering content with the default language: {locale}.",
            description:
              "Notification copy for missing localization, 'locale' = the name of the configured default language",
          },
          {
            locale: <strong>{props.def}</strong>,
          }
        )}
      </p>
    </Notification>
  );
}
